import { BrowserModule } from '@angular/platform-browser';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import { BctTitleStrategy } from './services/bct-title.strategy';
import {ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatOptionModule} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FailedComponent } from './failed/failed.component';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent, ProtectedResourceScopes } from '@azure/msal-angular';
import { NgxPermissionsModule } from 'ngx-permissions';
import { environment } from 'src/environments/environment';
import { UserService } from './services/user.service';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatIconModule} from '@angular/material/icon';
import {MatCardModule} from '@angular/material/card';
import {ModulesService} from './services/modules.service';
import {CorporateFormsService} from './corporate/services/corporate-forms.service';
import { PopupComponent } from './utils/popup/popup.component';
import {ExpandedVerticalMenuComponent} from './menu/expanded-vertical-menu/expanded-vertical-menu.component';
import {NgOptimizedImage} from '@angular/common';
import {FooterComponent} from './menu/footer/footer.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialogModule} from '@angular/material/dialog';
import { ConfirmDialogComponent } from './corporate/confirm-dialog/confirm-dialog.component';
import {InvoicingModuleMenuComponent} from './invoicing-module/invoicing-module-menu/invoicing-module-menu.component';

import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';

import { MatBadgeModule } from '@angular/material/badge';
import { MatSnackBarModule } from '@angular/material/snack-bar'; // Ensure this is imported
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatProgressBarModule} from '@angular/material/progress-bar';

import {
  msalCustomersSignIn
} from './auth/msal/config/msal-customers.config';
import {MsalCustomerInterceptor} from './auth/msal/interceptors/msal-customer.interceptor';
import {TenantContextService} from './services/tenant-context.service';
import {applyOrgQueryParam} from './services/org-scope-store';
import {OrgSwitchReuseStrategy, OrgSwitchService} from './shared/org-identity/org-switch.service';
import {RouteReuseStrategy} from '@angular/router';
import {NewUiService} from './core/ui/new-ui.service';
import {applyChartDefaults} from './shared/ui/chart-palette';
import {BcAppShellComponent} from './menu/app-shell/bc-app-shell.component';
import {BcAppSwitcherComponent} from './shared/ui/app-switcher/bc-app-switcher.component';
import {
  BcOrgBadgeComponent, BcOrgGroupComponent, BcOrgLayoutToggleComponent, BcOrgLensComponent, BcOrgModeComponent,
} from './shared/org-identity';
import {BcTabBarComponent} from './shared/ui/tab-bar/bc-tab-bar.component';
import {A11yModule} from '@angular/cdk/a11y';
import {CustomersLoginMsalService} from './auth/msal/services/customers-login-msal.service';
import {RedirectHandlerService} from './auth/services/test/redirect-handler.service';
import {InvoicingModuleService} from './invoicing-module/service/invoicing-module.service';
import { PlutonInvoicingModuleMenuComponent } from './pluton-invoicing-module/invoicing-module-menu/invoicing-module-menu.component';
import { PlutonInvoicingModuleService } from './pluton-invoicing-module/service/invoicing-module.service';
import { AuthRepresentativesComponent } from './corporate/documents/auth-representatives/auth-representatives.component';
import { PhonebookModule } from './corporate/phonebook';
import { MeetingNotesPanelComponent } from './corporate/meeting-notes/meeting-notes-panel/meeting-notes-panel.component';
import { OutlookPanelComponent } from './corporate/outlook';
import { ActiveTimersComponent } from './shared/components/active-timers/active-timers.component';
import { ReportWidgetComponent } from './ticketing/report-widget/report-widget.component';
import { PageFinderComponent } from './shared/page-finder/page-finder.component';
import { ticketingCaptureInterceptor } from './ticketing/services/console-capture.service';
import { AttendanceCheckInComponent } from './shared/components/attendance-check-in/attendance-check-in.component';
import { TeamPresenceComponent } from './shared/components/team-presence/team-presence.component';
import { NotificationDropdownComponent } from './notifications/notification-dropdown/notification-dropdown.component';
import { NavLinkDirective } from './shared/context-menu/nav-link.directive';
import { NotificationsPageComponent } from './notifications/notifications-page/notifications-page.component';
import { ReadOnlyInterceptor } from './permissions/read-only.interceptor';
import { entityHeaderInterceptor } from './accounting-module/accounting/interceptors/entity-token.interceptor';
import { clientListCacheInterceptor } from './corporate/services/client-list-cache.service';
import { signatureFixturesInterceptor } from './signatures/fixtures/signature-fixtures.interceptor';
import { clientErrorInterceptor, ReportingErrorHandler } from './services/client-error-reporter';
import { duplicateClientInterceptor } from './corporate/services/duplicate-client-guard.service';
import { ReadOnlyBannerComponent } from './permissions/read-only-banner.component';
import { ApimKeyBackend, apimSubscriptionKeyInterceptor } from './core/apim/apim-subscription';
// Render every Angular DatePipe date as dd/MM/yyyy by default (en-GB).
registerLocaleData(localeEnGb);

export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain]
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    }
  });
}

export function LoginMSALCustomersInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalCustomersSignIn);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  //const protectedResourceMap = new Map<string, Array<string>>();
  const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

    protectedResourceMap.set(environment.apiConfig.IdentityAPI.endpoint, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

    protectedResourceMap.set(environment.apiConfig.corporateAPI.endpoint, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

    // Ticketing (BC_Ticketing_Mod). Same Identity scopes as the other modules — the API validates the
    // B2C token and then asks BC_Identity for the caller's Ticketing permissions itself, so there is
    // no ticketing-specific scope to request here.
    protectedResourceMap.set(environment.apiConfig.ticketingAPI.endpoint, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

    protectedResourceMap.set(environment.invoicing_api, [
      {
        httpMethod: 'POST',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PUT',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'GET',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.read]
      },
      {
        httpMethod: 'DELETE',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      },
      {
        httpMethod: 'PATCH',
        scopes: [...environment.apiConfig.IdentityAPI.scopes.write]
      }
    ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      //scopes: [...environment.apiConfig.scopes],
      scopes: [],
    },
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({ declarations: [
        AppComponent,
        FailedComponent,
        BcAppShellComponent,
        PopupComponent,
        ConfirmDialogComponent,
        NotificationDropdownComponent,
        NotificationsPageComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
        MatNativeDateModule,
        BrowserModule,
        NavLinkDirective,
        MatMenuModule,
        MatDividerModule,
        MatBadgeModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        MatSnackBarModule,
        MatInputModule,
        MatButtonModule,
        MatToolbarModule,
        MatListModule,
        MatTableModule,
        MatSelectModule,
        MatOptionModule,
        MatFormFieldModule,
        MatStepperModule,
        ReactiveFormsModule,
        MsalModule,
        NgxPermissionsModule.forRoot(),
        MatAutocompleteModule,
        MatIconModule,
        MatCardModule,
        FormsModule,
        ExpandedVerticalMenuComponent,
        NgOptimizedImage,
        FooterComponent,
        MatTooltipModule,
        MatDialogModule,
        InvoicingModuleMenuComponent,
        PlutonInvoicingModuleMenuComponent,
        MatDatepickerModule,
        MatNativeDateModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        PhonebookModule,
        MeetingNotesPanelComponent,
        OutlookPanelComponent,
        ActiveTimersComponent,
        ReportWidgetComponent,
        PageFinderComponent,
        AttendanceCheckInComponent,
        TeamPresenceComponent,
        ReadOnlyBannerComponent,
        BcAppSwitcherComponent,
        BcOrgBadgeComponent,
        BcOrgLensComponent,
        BcOrgModeComponent,
        BcOrgLayoutToggleComponent,
        BcOrgGroupComponent,
        // App shell on phones and tablets: bottom tabs, and the focus trap of the drawer.
        BcTabBarComponent,
        A11yModule], providers: [
        // Names each browser tab after the page that is open, instead of every tab reading "Bizcombo".
        {
            provide: TitleStrategy,
            useClass: BctTitleStrategy,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalCustomerInterceptor,
            multi: true,
        },
        // Registered AFTER the MSAL interceptor so a blocked write short-circuits before a token is
        // acquired for it. Rejects PUT/PATCH/DELETE from users holding the read-only Viewer role in the
        // module that owns the endpoint — see the class comment for why POST is deliberately untouched.
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ReadOnlyInterceptor,
            multi: true,
        },
        // {
        //   provide: HTTP_INTERCEPTORS,
        //   useClass: MsalInterceptor,
        //   multi: true,
        // },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: 'MSAL_CUSTOMERS_LOGIN',
            useFactory: LoginMSALCustomersInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        {
            // Use this HttpClient For Anonymous Request
            provide: 'NoInterceptorHttpClient',
            // No auth/tenant interceptors — but the APIM subscription key is still added, or APIM 401s it.
            useFactory: (handler: HttpBackend) => new HttpClient(new ApimKeyBackend(handler)),
            deps: [HttpBackend],
        },
        {
            provide: LOCALE_ID,
            useValue: 'en-GB'
        },
        {
            // Default format for {{ value | date }} when no explicit format is passed.
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: { dateFormat: 'dd/MM/yyyy' }
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB'
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: { day: 'numeric', month: 'numeric', year: 'numeric' },
                },
                display: {
                    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
                    monthYearLabel: { month: 'short', year: 'numeric' },
                    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
                    monthYearA11yLabel: { month: 'long', year: 'numeric' },
                },
            }
        },
        // These three run in registration order, exactly as the APP_INITIALIZER
        // multi-provider they replace did. provideAppInitializer takes the initializer
        // itself rather than a factory returning one, so the extra `() =>` wrapper the
        // old form needed is gone, and dependencies come from inject() because the
        // callback runs inside an injection context.
        // The cast is needed because @azure/msal-angular 3.x declares
        // `MSAL_INSTANCE: InjectionToken<string>` even though MSALInstanceFactory above
        // provides an IPublicClientApplication. The old `deps: [MSAL_INSTANCE]` form hid
        // this - deps is untyped, so the factory's parameter type was an unchecked
        // assertion. inject() actually type-checks, which is how it surfaced. Revisit
        // when we move to msal-angular 6.
        provideAppInitializer(() =>
            (inject(MSAL_INSTANCE) as unknown as IPublicClientApplication).initialize()),
        // Resolve the tenant from the subdomain BEFORE the app makes any API call, so the
        // interceptor can stamp X-BC-Tenant and module gating knows what's licensed.
        provideAppInitializer(() => inject(TenantContextService).bootstrap()),
        // Adopt the `?org=<companyId>` param (set when an organization is opened in a new
        // tab) into this tab's per-tab store BEFORE the router/guards run, so permission
        // loads target the right company. Synchronous — no API call, so ordering is safe.
        provideAppInitializer(() => { applyOrgQueryParam(); }),
        // Org tabs switch this tab's organisation in place (docs/multi-org); the strategy recreates
        // the routed pages on the navigation that follows, so they load for the new organisation.
        provideAppInitializer(() => { inject(OrgSwitchService); }),
        { provide: RouteReuseStrategy, useClass: OrgSwitchReuseStrategy },
        // Option D redesign: puts `bc-new-ui` on <html> (the redesign styles' scope) before the
        // first render. Synchronous.
        provideAppInitializer(() => { inject(NewUiService).init(); }),
        // Option D chart defaults (axis text, grid, font) for every Chart.js chart.
        provideAppInitializer(() => { applyChartDefaults(); }),
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        UserService,
        ModulesService,
        CorporateFormsService,
        InvoicingModuleService,
        PlutonInvoicingModuleService,
        // The accounting module's functional interceptor stamps X-Entity-Id on calls to the
        // accounting API (and only those — it matches the resolved API base). Listed before
        // withInterceptorsFromDi so a request with no active entity is rejected before the
        // MSAL interceptor bothers acquiring a token for it.
        // signatureFixturesInterceptor answers bc-signatures-api out of memory while that service
        // does not exist. It is inert unless environment.useSignatureFixtures is true (dev only),
        // and it is listed first so a fixtured request short-circuits before the MSAL interceptor
        // tries to acquire a token for a scope that has not been registered yet.
        // clientErrorInterceptor goes first so it sees what the whole chain produced, including a
        // request the entity or MSAL interceptors refused before it was ever sent.
        // duplicateClientInterceptor opens the "client already exists" modal for any create that
        // BC_Corporate refused as a duplicate, whichever screen sent it.
        provideHttpClient(
          withInterceptors([apimSubscriptionKeyInterceptor, clientErrorInterceptor, duplicateClientInterceptor, signatureFixturesInterceptor, entityHeaderInterceptor, clientListCacheInterceptor, ticketingCaptureInterceptor]),
          withInterceptorsFromDi(),
        ),
        // Uncaught errors in the page are reported to BC_Corporate as well as logged to the console.
        { provide: ErrorHandler, useClass: ReportingErrorHandler },
        // ng2-charts v8 dropped NgChartsModule, and with it the implicit Chart.js
        // controller registration. Without this the charts build fine and then render
        // nothing at runtime.
        provideCharts(withDefaultRegisterables())
    ] })
export class AppModule { }
